import { useAtom, useAtomValue } from 'jotai';
import { Box, Flex, Menu, MenuContent, MenuTrigger, Text } from '@optiaxiom/react';
import { IconCircleHalfStrokeSolid, IconText } from '@optimizely/axiom-icons';

import { type FontSize, fontSizeAtom, type Theme, themeAtom } from '@frontend/atoms/appearanceAtom';
import { useObjectTitle, useOpalUser, useSetFontSizeWithPersist } from '@frontend/hooks';

import { styles } from '../Settings.css';

type FontSizeOption = { value: FontSize; label: string; description: string };
type ThemeOption = { value: Theme; label: string };

const FONT_SIZE_OPTIONS: FontSizeOption[] = [
  {
    value: 'default',
    label: 'Default',
    description: 'Standard font size for Opal chat.',
  },
  {
    value: 'comfort',
    label: 'Comfort',
    description: 'Larger font size and line-height for easier reading.',
  },
];

const THEME_OPTIONS: ThemeOption[] = [
  {
    value: 'dark',
    label: 'Dark mode',
  },
  {
    value: 'light',
    label: 'Light mode',
  },
];

export const AppearanceConfiguration = () => {
  useObjectTitle('Appearance');
  const fontSize = useAtomValue(fontSizeAtom);
  const [theme, setTheme] = useAtom(themeAtom);
  const { opalUser } = useOpalUser();
  const setFontSize = useSetFontSizeWithPersist(opalUser.email);

  const handleThemeChange = (newTheme: Theme) => {
    if ('startViewTransition' in document && typeof document.startViewTransition === 'function') {
      document.startViewTransition(() => {
        setTheme(newTheme);
      });
    } else {
      setTheme(newTheme);
    }
  };

  return (
    <Flex flexDirection="column" gap="32" mt="8" className={styles.settingsContainer}>
      <Flex
        flexDirection="row"
        gap="8"
        alignItems="center"
        justifyContent="space-between"
        p="16"
        bg="bg.default"
        rounded="xl"
      >
        <Flex flexDirection="row" gap="12" alignItems="center">
          <Box color="fg.tertiary">
            <IconText size="20" />
          </Box>
          <Flex flexDirection="column" gap="2">
            <Text fontWeight="500">Font Size</Text>
            <Text fontSize="sm" color="fg.tertiary" className={styles.settingsDescription}>
              Adjust the chat font size to your reading preference for Opal chat.
            </Text>
          </Flex>
        </Flex>
        <Menu
          options={FONT_SIZE_OPTIONS.map(option => ({
            label: option.label,
            description: option.description,
            execute: () => setFontSize(option.value),
          }))}
        >
          <MenuTrigger>
            {FONT_SIZE_OPTIONS.find(opt => opt.value === fontSize)?.label || 'Default'}
          </MenuTrigger>
          <MenuContent />
        </Menu>
      </Flex>

      <Flex
        flexDirection="row"
        gap="8"
        alignItems="center"
        justifyContent="space-between"
        p="16"
        bg="bg.default"
        rounded="xl"
      >
        <Flex flexDirection="row" gap="12" alignItems="center">
          <Box color="fg.tertiary">
            <IconCircleHalfStrokeSolid size="20" />
          </Box>
          <Flex flexDirection="column" gap="2">
            <Text fontWeight="500">Theme</Text>
            <Text fontSize="sm" color="fg.tertiary" className={styles.settingsDescription}>
              Switch between light and dark modes.
            </Text>
          </Flex>
        </Flex>
        <Menu
          options={THEME_OPTIONS.map(option => ({
            label: option.label,
            execute: () => handleThemeChange(option.value),
          }))}
        >
          <MenuTrigger>
            {THEME_OPTIONS.find(opt => opt.value === theme)?.label || 'Light mode'}
          </MenuTrigger>
          <MenuContent />
        </Menu>
      </Flex>
    </Flex>
  );
};
